import { transformProperties } from "@/utils/simplifyData"
import styled from "@emotion/styled"
import { Box, Button, IconButton, Menu, MenuItem } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import Link from "next/link"
import { useState } from "react"
import { extractMenuProperties, getNonUmbracoUrl } from "utils"
import { iconsMap } from "../../../public/icons/iconsMap"
import ButtonWithArrow from "../../common/bit/ButtonWithArrow"
import { Desktop, Mobile } from "../../common/bit/MediaQueries"
import MegaMenu from "../../Nav/MegaMenu/MegaMenu"
import MobileMenu from "../../Nav/MegaMenu/MobileMenu"
import shared from "../../theme/shared"
import { colors, mediaQueries, sizes } from "../../theme/theme"

const dropdownMenuWidth = "201px"

const DesktopHeaderWrapper = styled(Box)`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
`

const StyledDesktopHeader = styled.div<{ isopen?: boolean }>`
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: ${sizes.maxWidthContainer};
  width: calc(100% - 2em);
  margin: auto 0;
  padding: 0 20px;
  margin-top: 18px;
  background: ${colors.white};
  box-shadow: ${colors.dropShadow.header};
  border-radius: 8px;
  z-index: 100;
  font-size: 14px;

  ${mediaQueries.tablet} {
    width: calc(100% - 32px);
    margin: 20px 16px;
    padding: 0 12px;
    flex-direction: ${(props) => (props.isopen ? "row-reverse" : "row")};
  }
  ${mediaQueries.desktopM} {
    height: ${shared.HEADER_HEIGHT_S}px;
    border-radius: ${sizes.radius.circle};
    max-width: 100%;
  }
  ${mediaQueries.desktop} {
    height: ${shared.HEADER_HEIGHT_S}px;
    border-radius: ${sizes.radius.circle};
    max-width: 100%;
  }
  ${mediaQueries.desktopXl} {
    height: ${shared.HEADER_HEIGHT_D}px;
    max-width: ${sizes.maxWidthContainerXl};
    font-size: 18px;
  }
`
const StyledMobileHeader = styled.div<{ isopen?: boolean }>`
  position: fixed;
  display: flex;
  flex-direction: ${(props) => (props.isopen ? "row-reverse" : "row")};
  align-items: center;
  justify-content: space-between;
  height: ${shared.HEADER_HEIGHT_S}px;
  width: calc(100% - 32px);
  padding: 0 12px;
  margin: 20px 16px;
  border-radius: 8px;
  background: ${colors.white};
  box-shadow: ${colors.dropShadow.header};
  z-index: 100;
`
const StyledMenuMobileWrapper = styled(Menu)`
  top: 0;
  .MuiPaper-root {
    top: 73px !important; // TODO: Fix this
    width: 100%;
    box-shadow: 0px 4px 16px 0px rgba(90, 90, 90, 0.05);
  }
  z-index: 100;
`
const StyledButtonsContainer = styled(Box)`
  display: flex;
  gap: 10px;
  margin-inline-start: auto;
`
const StyledButton = styled(Button)`
  font-size: 14px;
  padding: 8px 14px 8px 10px;
  ${mediaQueries.tabletDown} {
    font-size: 14px;
  }
  ${mediaQueries.desktopM} {
    font-size: 14px;
    padding: 8px 14px 8px 10px;
  }
  ${mediaQueries.desktop} {
    font-size: 14px;
    padding: 13px 18px 13px 14px;
  }
  ${mediaQueries.desktopXl} {
    font-size: 16px;
  }
`
const StyledButtonWithArrow = styled(ButtonWithArrow)`
  font-size: 14px;
  padding: 8px 14px 8px 10px;
  white-space: pre;
  ${mediaQueries.tabletDown} {
    font-size: 14px;
  }
  ${mediaQueries.desktopM} {
    font-size: 14px;
    padding: 8px 14px 8px 10px;
  }
  ${mediaQueries.desktop} {
    font-size: 14px;
    padding: 8px 20px 8px 16px;
  }
  ${mediaQueries.desktopXl} {
    font-size: 16px;
  }
`

const StyledRightSide = styled(Box)`
  display: flex;
  align-items: center;
  gap: 20px;
`
const StyledIconButton = styled(IconButton)`
  display: flex;
  svg {
    display: flex;
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    stroke: ${colors.black};
  }
  ${mediaQueries.mobile} {
    padding: 0;
  }
`
const StyledLeftSide = styled(Box)`
  display: flex;
  gap: 12px;
  width: 92px;
  gap: 12px;
`
const StyledIconAndText = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  color: ${colors.black};
  font-size: 10px;
  text-decoration: none;
  white-space: pre;
  gap: 0;
  svg {
    &.phone {
      stroke: ${colors.black};
      fill: ${colors.black};
    }
  }
`

const ImageWrapper = styled(Box)`
  width: 94px;
  height: 24px;
  position: relative;

  ${mediaQueries.desktopXl} {
    width: 159px;
    height: 40px;
  }
`

const StyledDropdownMenu = styled(Menu)`
  .MuiPaper-root {
    display: flex;
    flex-direction: column;
    background-color: ${colors.white};
    border: 1px solid ${colors.red};
    border-top: none;
    border-radius: 0 0 22px 22px;
    margin-top: -20px;
    padding: 0;
    overflow: visible;
    box-shadow: none;
    width: ${dropdownMenuWidth};
  }
  .MuiList-root {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 8px 0;
  }
  .MuiMenuItem-root {
    font-size: 16px;
    padding: 12px 20px;
    color: ${colors.black};
    transition: background-color 0.2s ease-in-out;
    text-align: center;
    justify-content: center;

    &:hover {
      background-color: ${colors.beige};
    }

    &:last-child {
      border-radius: 0 0 22px 22px;
    }
  }
`

const StyledDropdownButton = styled(Button)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding: 8px 14px 8px 10px;
  gap: 8px;
  white-space: nowrap;
  border: 1px solid ${colors.red};
  border-radius: ${sizes.borderRadiusLarge};
  color: ${colors.black};
  background-color: ${colors.white};
  width: ${dropdownMenuWidth};
  .MuiButton-icon {
    margin-left: unset;
  }
  &:hover {
    background-color: ${colors.white};
    border: 1px solid ${colors.red};
  }

  ${mediaQueries.desktopM} {
    font-size: 14px;
    padding: 8px 14px 8px 10px;
  }
  ${mediaQueries.desktop} {
    font-size: 14px;
    padding: 13px 18px 13px 14px;
  }
  ${mediaQueries.desktopXl} {
    font-size: 16px;
  }
`

const Header = ({ header_footer_data }: { header_footer_data: any }) => {
  const BurgerIcon: React.FC = iconsMap["Hamburger"]
  const SearchIcon: React.FC = iconsMap["search"]
  const PersonalArea: React.FC = iconsMap["personalArea"]
  const PhoneIcon = iconsMap["phone"]
  const HamburgerClose: React.FC = iconsMap["HamburgerClose"]
  const BackArrow: React.FC = iconsMap["backArrow"]
  const ChevronDownIcon = iconsMap["ChevronDownIcon"]
  const t = useTranslations("Header")

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openMobileMenu, setOpenMobileMenu] = useState(false)
  const [selectedInnerMenu, setSelectedInnerMenu] = useState({} as any)
  const [openSearch, setOpenSearch] = useState(false)
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState<null | HTMLElement>(
    null,
  )

  const handleMobileClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setOpenMobileMenu((prev) => !prev)
  }

  const handleClose = () => {
    setOpenMobileMenu(false)
  }

  const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDropdownAnchorEl(event.currentTarget)
  }

  const handleDropdownClose = () => {
    setDropdownAnchorEl(null)
  }

  const propertiesToDisplay = extractMenuProperties(
    header_footer_data?.header,
  ).clickToCall
  const logoUrl = extractMenuProperties(header_footer_data?.header).siteLogo
  const personalArea = transformProperties(
    header_footer_data?.header?.properties,
  )?.personalAreaLink?.links?.[0]

  const moreSites = transformProperties(header_footer_data?.header?.properties)
    ?.moreSites?.blocks?.[0]
  const parsedMoreSites = transformProperties(moreSites?.properties)
  const dropdownOpen = Boolean(dropdownAnchorEl)

  return (
    <>
      <Desktop>
        <DesktopHeaderWrapper>
          <StyledDesktopHeader className="mui-fixed">
            {logoUrl && (
              <Link href="/">
                <ImageWrapper>
                  <Image src={logoUrl} alt="Logo" fill priority />
                </ImageWrapper>
              </Link>
            )}
            <MegaMenu
              data={header_footer_data?.header}
              propertiesToDisplay={propertiesToDisplay}
            />
            <StyledButtonsContainer>
              {!!parsedMoreSites && (
                <>
                  <StyledDropdownButton
                    variant="outlined"
                    onClick={handleDropdownOpen}
                    aria-controls={dropdownOpen ? "agents-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={dropdownOpen ? "true" : undefined}
                    endIcon={<ChevronDownIcon />}
                  >
                    {parsedMoreSites.title?.value || t("agents")}
                  </StyledDropdownButton>
                  <StyledDropdownMenu
                    id="agents-menu"
                    anchorEl={dropdownAnchorEl}
                    open={dropdownOpen}
                    onClose={handleDropdownClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    {parsedMoreSites.dropdownList?.links?.map(
                      (item: any, index: number) => (
                        <MenuItem
                          key={`dropdown-item-${index}`}
                          component="a"
                          href={getNonUmbracoUrl(item.url)}
                          target={item.target || "_self"}
                          onClick={handleDropdownClose}
                        >
                          {item.name ||
                            (index === 0
                              ? parsedMoreSites.title?.value
                              : t("agents"))}
                        </MenuItem>
                      ),
                    )}
                  </StyledDropdownMenu>
                </>
              )}
              {!!personalArea && (
                <StyledButtonWithArrow
                  href={getNonUmbracoUrl(personalArea?.url)}
                >
                  {personalArea?.name}
                </StyledButtonWithArrow>
              )}
            </StyledButtonsContainer>
          </StyledDesktopHeader>
        </DesktopHeaderWrapper>
      </Desktop>
      <Mobile>
        <StyledMobileHeader isopen={openMobileMenu}>
          <StyledRightSide>
            <IconButton onClick={handleMobileClick}>
              {openMobileMenu ? <HamburgerClose /> : <BurgerIcon />}
            </IconButton>
            {/* {!openMobileMenu && ( //TODO: Uncomment when search is ready
              <StyledIconButton
                onClick={() => setOpenSearch((prevState) => !prevState)}
              >
                <SearchIcon />
              </StyledIconButton>
            )} */}
          </StyledRightSide>
          {!selectedInnerMenu?.children?.length ? (
            <>
              {logoUrl && (
                <Link href="/">
                  <Image
                    src={logoUrl}
                    alt="Logo"
                    width={102}
                    height={26}
                    priority
                  />
                </Link>
              )}
            </>
          ) : (
            <IconButton
              onClick={(e) => {
                setSelectedInnerMenu({})
                e.stopPropagation()
              }}
            >
              <BackArrow />
            </IconButton>
          )}

          {!openMobileMenu && (
            <StyledLeftSide>
              <StyledIconAndText
                href={
                  extractMenuProperties(propertiesToDisplay[0]).phoneNumber[0]
                    ?.url || "/"
                }
              >
                <PhoneIcon width="22" height="22" className="phone" />
                {
                  extractMenuProperties(propertiesToDisplay[0]).phoneNumber[0]
                    ?.name
                }
              </StyledIconAndText>
              {!!personalArea && (
                <StyledIconAndText href={getNonUmbracoUrl(personalArea?.url)}>
                  <PersonalArea />
                  {personalArea?.name}
                </StyledIconAndText>
              )}
            </StyledLeftSide>
          )}
        </StyledMobileHeader>
        <StyledMenuMobileWrapper
          open={openMobileMenu}
          onClose={handleClose}
          anchorEl={anchorEl}
        >
          <MobileMenu
            selectedInnerMenu={selectedInnerMenu}
            setSelectedInnerMenu={setSelectedInnerMenu}
            data={header_footer_data?.header}
            handleClose={handleClose}
          />
        </StyledMenuMobileWrapper>
      </Mobile>
    </>
  )
}

export default Header
